import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Chip,
    IconButton,
    Pagination,
    Breadcrumbs,
    Link as MuiLink
} from '@mui/material';
import { Print as PrintIcon, Edit as EditIcon, Add as AddIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { getRunSheets, dispatchRunSheet, checkInRunSheet, printRunSheet, RunSheet } from '../../services/runSheetApi';
import GenericModal from '../../components/GenericModal';
import useToast from '../../hooks/useToast';
import Toast from '../../components/Toast';

const RunSheets = () => {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const [runSheets, setRunSheets] = useState<RunSheet[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const { toast, showSuccess, showError, hideToast } = useToast();
    const [confirmModal, setConfirmModal] = useState<{ open: boolean; title: string; message: string; onConfirm: () => void }>({ open: false, title: '', message: '', onConfirm: () => {} });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchRunSheets();
    }, [page]);

    const fetchRunSheets = async () => {
        try {
            setLoading(true);
            const response = await getRunSheets({ page, per_page: 20 });
            setRunSheets(response.data.data);
            setTotalPages(response.data.last_page || 1);
        } catch (error: any) {
            console.error('Error fetching run sheets:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDispatch = (id: number) => {
        setConfirmModal({
            open: true,
            title: 'Dispatch Run Sheet',
            message: 'Are you sure you want to dispatch this run sheet?',
            onConfirm: async () => {
                setSubmitting(true);
                try {
                    await dispatchRunSheet(id);
                    setConfirmModal(prev => ({ ...prev, open: false }));
                    showSuccess('Run sheet dispatched successfully!');
                    fetchRunSheets();
                } catch (error: any) {
                    showError(error.message || 'Failed to dispatch run sheet');
                } finally {
                    setSubmitting(false);
                }
            },
        });
    };

    const handleCheckIn = (id: number) => {
        setConfirmModal({
            open: true,
            title: 'Check In Run Sheet',
            message: 'Are you sure you want to check in this run sheet?',
            onConfirm: async () => {
                setSubmitting(true);
                try {
                    await checkInRunSheet(id);
                    setConfirmModal(prev => ({ ...prev, open: false }));
                    showSuccess('Run sheet checked in successfully!');
                    fetchRunSheets();
                } catch (error: any) {
                    showError(error.message || 'Failed to check in run sheet');
                } finally {
                    setSubmitting(false);
                }
            },
        });
    };

    const getStatusColor = (statusName: string) => {
        const lower = statusName.toLowerCase();
        if (lower.includes('created')) return 'warning';
        if (lower.includes('dispatch')) return 'info';
        if (lower.includes('checked')) return 'primary';
        if (lower.includes('closed')) return 'success';
        return 'default';
    };

    return (
        <Box>
            <Breadcrumbs sx={{ mb: 3 }}>
                <MuiLink
                    component="button"
                    variant="body1"
                    onClick={() => navigate('/dashboard')}
                    sx={{ textDecoration: 'none', color: theme.palette.text.primary }}
                >
                    Home
                </MuiLink>
                <Typography color="text.primary">Run Sheets</Typography>
            </Breadcrumbs>

            <Card>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>
                            Run Sheets
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={() => navigate('/run-sheets/create')}
                        >
                            Add New
                        </Button>
                    </Box>

                    <TableContainer component={Paper} variant="outlined">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Run Sheet ID</TableCell>
                                    <TableCell>Rider</TableCell>
                                    <TableCell>Master Units</TableCell>
                                    <TableCell>Created At</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">Loading...</TableCell>
                                    </TableRow>
                                ) : runSheets.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">No records found</TableCell>
                                    </TableRow>
                                ) : (
                                    runSheets.map((sheet) => (
                                        <TableRow key={sheet.id}>
                                            <TableCell>
                                                <MuiLink
                                                    component="button"
                                                    onClick={() => navigate(`/run-sheets/${sheet.id}`)}
                                                    sx={{ textDecoration: 'none' }}
                                                >
                                                    {sheet.unique_id}
                                                </MuiLink>
                                            </TableCell>
                                            <TableCell>{sheet.rider?.name}</TableCell>
                                            <TableCell>{sheet.masterunits?.length || 0}</TableCell>
                                            <TableCell>{new Date(sheet.created_at).toLocaleString()}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={sheet.status.name}
                                                    color={getStatusColor(sheet.status.name) as any}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <IconButton 
                                                    size="small" 
                                                    color="info"
                                                    onClick={() => printRunSheet(sheet.id)}
                                                >
                                                    <PrintIcon />
                                                </IconButton>
                                                {sheet.status_id === 8 && (
                                                    <Button 
                                                        size="small" 
                                                        variant="contained" 
                                                        color="warning" 
                                                        onClick={() => handleDispatch(sheet.id)}
                                                        sx={{ ml: 1 }}
                                                    >
                                                        Dispatch
                                                    </Button>
                                                )}
                                                {sheet.status_id === 21 && (
                                                    <Button 
                                                        size="small" 
                                                        variant="contained" 
                                                        color="warning" 
                                                        onClick={() => handleCheckIn(sheet.id)}
                                                        sx={{ ml: 1 }}
                                                    >
                                                        Check-in
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(e, value) => setPage(value)}
                                color="primary"
                            />
                        </Box>
                    )}
                </CardContent>
            </Card>

            <GenericModal
                open={confirmModal.open}
                onClose={() => setConfirmModal(prev => ({ ...prev, open: false }))}
                title={confirmModal.title}
                maxWidth="xs"
                actions={
                    <>
                        <Button
                            onClick={() => setConfirmModal(prev => ({ ...prev, open: false }))}
                            disabled={submitting}
                            sx={{ color: theme.palette.text.secondary, borderRadius: 3 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            onClick={confirmModal.onConfirm}
                            disabled={submitting}
                            sx={{ borderRadius: 3 }}
                        >
                            {submitting ? 'Processing...' : 'Yes, Proceed'}
                        </Button>
                    </>
                }
            >
                <Typography variant="body1" color="text.primary">
                    {confirmModal.message}
                </Typography>
            </GenericModal>

            <Toast
                open={toast.open}
                message={toast.message}
                title={toast.title}
                severity={toast.severity}
                onClose={hideToast}
            />
        </Box>
    );
};

export default RunSheets;



